import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, input, output, signal, ViewChild } from '@angular/core';
import { SewButton } from '@sew/ui-components-angular/standalone';
import { DefaultTooltipComponent } from '../../directives/tooltip/default-tooltip/default-tooltip.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';

interface NamePlateSearchResponse {
    sn: string[];
    text: string;
}

const allowedImageMimeTypes = new Set([
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/tiff',
    'image/webp',
]);

const maxImageFileSizeBytes = 10 * 1024 * 1024;

export interface NamePlateSearchResult {
    serialNumber?: string;
    noSerialNumberFound?: boolean;
    invalidFileFormat?: boolean;
    fileSizeExceeded?: boolean;
    unknownError?: boolean;
}

@Component({
    selector: 'osc-nameplate-search',
    templateUrl: './nameplate-search.component.html',
    imports: [SewButton, TooltipDirective, DefaultTooltipComponent],
})
export class NameplateSearchComponent {
    apiKey = input.required<string>();
    apiUrl = input.required<string>();
    tooltipText = input.required<string | null>();
    icon = input('camera-document');
    searchResult = output<NamePlateSearchResult>();

    @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

    public uploadInProgress = signal(false);

    public constructor(private http: HttpClient) {}

    public openFilePicker(): void {
        this.fileInput?.nativeElement.click();
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        // Clear the input's value so selecting the same file again will trigger a change
        // in all browsers (prevents the input from ignoring identical consecutive selections).
        try {
            input.value = '';
            if (this.fileInput?.nativeElement) {
                this.fileInput.nativeElement.value = '';
            }
        } catch (e) {
            // ignore: some environments may treat value as read-only
        }
        if (!file) {
            return;
        }

        if (!allowedImageMimeTypes.has(file.type.toLowerCase())) {
            this.searchResult.emit({ invalidFileFormat: true });

            return;
        }

        if (file.size > maxImageFileSizeBytes) {
            this.searchResult.emit({ fileSizeExceeded: true });

            return;
        }

        this.uploadInProgress.set(true);

        const formData = new FormData();
        formData.append('imgData', file, file.name);

        const options = this.apiKey()
            ? {
                  headers: new HttpHeaders({
                      'x-Gateway-APIKey': this.apiKey(),
                  }),
              }
            : undefined;

        this.http.post<NamePlateSearchResponse>(this.apiUrl(), formData, options).subscribe({
            next: (resp) => {
                this.uploadInProgress.set(false);
                if (resp && resp.sn[0]) {
                    this.searchResult.emit({ serialNumber: resp.sn[0] });
                } else {
                    this.searchResult.emit({ noSerialNumberFound: true });
                }
            },
            error: (err: HttpErrorResponse) => {
                this.uploadInProgress.set(false);
                if (err.status === 413) {
                    this.searchResult.emit({ fileSizeExceeded: true });
                } else if (err.status === 404) {
                    this.searchResult.emit({ noSerialNumberFound: true });
                } else if (err.status === 400) {
                    this.searchResult.emit({ invalidFileFormat: true });
                } else {
                    console.error('Unexpected error');
                    this.searchResult.emit({ unknownError: true });
                }
            },
        });
    }
}
